import "@/styles/application.css";
import { createRoot } from "react-dom/client";
import { createInertiaApp, router } from "@inertiajs/react";
import { Layout } from "@/components/layouts/UserLayout";
import { LoginLayout } from "@/components/layouts/LoginLayout";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { StaffLayout } from "@/components/layouts/StaffLayout";
import { SetupLayout } from "@/components/layouts/SetupLayout";
import { toast } from "sonner";
import React from "react";
import { initNativeApp } from "@/lib/native-bridge";
import { syncTrackingSlug } from "@/lib/trackingSlug";
import { captureReferrerDomain, flushReferrerDomain } from "@/lib/referrerDomain";
import { initSentry } from "@/lib/sentry";
import getClient from "@/apis/client";

// Sentry の初期化（VITE_SENTRY_DSN が未設定なら自動的にスキップ）
initSentry();

// ネイティブアプリの初期化（Capacitor環境でのみ実行）
initNativeApp().catch(console.error);

// 計測URL slug を Cookie / localStorage 双方で保持し、片方が消えた場合に復元する
syncTrackingSlug();

// 流入元 referrer の host を first-touch で localStorage に保存
captureReferrerDomain();

// 動的インポート失敗時の自動リロード（デプロイ後のアセット不整合対策）
const RELOAD_COOLDOWN_KEY = "asset_reload_timestamp";
const RELOAD_COOLDOWN_MS = 10_000; // 10秒以内の連続リロードを防止

function shouldAutoReload(): boolean {
  const lastReload = sessionStorage.getItem(RELOAD_COOLDOWN_KEY);
  if (lastReload) {
    const elapsed = Date.now() - parseInt(lastReload, 10);
    if (elapsed < RELOAD_COOLDOWN_MS) {
      console.warn("[AssetReload] Skipping reload - cooldown active");
      return false;
    }
  }
  return true;
}

function performAssetReload(): void {
  if (!shouldAutoReload()) {
    toast.error(
      "ページの読み込みに問題が発生しました。ブラウザを更新してください。"
    );
    return;
  }

  console.log("[AssetReload] New assets detected. Reloading page...");
  sessionStorage.setItem(RELOAD_COOLDOWN_KEY, Date.now().toString());
  window.location.reload();
}

// グローバルエラーハンドラー：動的インポート失敗をキャッチ
window.addEventListener("error", (event) => {
  if (
    event.message?.includes("Failed to fetch dynamically imported module") ||
    event.message?.includes("Loading chunk") ||
    event.message?.includes("Loading CSS chunk")
  ) {
    event.preventDefault();
    performAssetReload();
  }
});

// Promiseの未処理リジェクションもキャッチ
window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason?.message || String(event.reason);
  if (
    reason?.includes("Failed to fetch dynamically imported module") ||
    reason?.includes("Loading chunk") ||
    reason?.includes("Loading CSS chunk")
  ) {
    event.preventDefault();
    performAssetReload();
  }
});

// Inertia.jsのグローバルエラーハンドリング
router.on("error", (event) => {
  // デフォルトのモーダル表示を抑制
  event.preventDefault();

  const response = (event.detail as any).response;
  const status = response?.status;

  // 409 Conflict = アセットバージョン不一致
  if (status === 409) {
    console.log("[Inertia] Asset version mismatch detected. Reloading...");
    const targetUrl =
      (event.detail as any).page?.url || window.location.pathname;
    sessionStorage.setItem("inertia_reload_redirect", targetUrl);
    performAssetReload();
    return;
  }

  // 401 Unauthorized = セッション切れ → ログインページへリダイレクト
  if (status === 401) {
    window.location.href = "/users/sign_in";
    return;
  }

  // 422 Unprocessable Entity = バリデーションエラー
  if (status === 422) {
    const errors = (event.detail as any).errors;
    if (errors && typeof errors === "object") {
      const firstError = Object.values(errors)[0];
      if (firstError) {
        const errorMessage = Array.isArray(firstError)
          ? firstError[0]
          : String(firstError);
        toast.error(errorMessage);
        return;
      }
    }
    toast.error("入力内容に問題があります。");
    return;
  }

  // 500 Internal Server Error
  if (status === 500) {
    toast.error("サーバーエラーが発生しました。しばらくしてから再度お試しください。");
    return;
  }

  // その他のエラー
  const errors = (event.detail as any).errors;
  if (errors && typeof errors === "object") {
    const firstError = Object.values(errors)[0];
    if (firstError) {
      const errorMessage = Array.isArray(firstError)
        ? firstError[0]
        : String(firstError);
      toast.error(errorMessage);
      return;
    }
  }

  toast.error("エラーが発生しました。");
});

// リロード後の遷移先復元
router.on("navigate", () => {
  const redirectUrl = sessionStorage.getItem("inertia_reload_redirect");
  if (redirectUrl && redirectUrl !== window.location.pathname) {
    sessionStorage.removeItem("inertia_reload_redirect");
    router.visit(redirectUrl);
  }
});

// ページ遷移成功時にflashメッセージをチェック
router.on("success", (event) => {
  const page = (event.detail as any).page;

  // CSRFトークンを更新（SPA操作中のトークン失効対策）
  if (page?.props?.csrf_token) {
    const csrfMeta = document.querySelector('meta[name="csrf-token"]');
    if (csrfMeta) {
      csrfMeta.setAttribute("content", page.props.csrf_token);
    }
  }

  // デバッグ: flashメッセージの確認
  if (process.env.NODE_ENV === "development") {
    console.log("[Flash]", page?.props?.flash);
  }

  // props.errorがあればToastで表示
  if (page?.props?.error) {
    toast.error(String(page.props.error));
  }

  // props.errorsオブジェクトもチェック
  if (page?.props?.errors) {
    const errors = page.props.errors;
    if (typeof errors === "object" && Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      if (firstError) {
        const errorMessage = Array.isArray(firstError)
          ? firstError[0]
          : String(firstError);
        toast.error(errorMessage);
      }
    }
  }

  // Railsのflashメッセージを自動的にToastで表示
  if (page?.props?.flash) {
    const flash = page.props.flash;

    if (flash.notice) {
      toast.success(String(flash.notice));
    }
    if (flash.alert) {
      toast.warning(String(flash.alert));
    }
    if (flash.error) {
      toast.error(String(flash.error));
    }
    if (flash.success) {
      toast.success(String(flash.success));
    }
  }
});


const noLayoutPages = ["Home/Index", "Home/LandingPage", "Home/Diagnostic27", "Home/LpLine", "Legal/Privacy", "Legal/Terms", "public/LineFriends/Landing", "public/LineFriends/Liff", "public/Surveys/Liff", "public/Shindan/Liff", "public/Shindan/Index", "public/Interviews/Liff", "public/Liff/Auth"];

// LoginLayoutを使用するページ（完全にシンプル、ヘッダーなし）
const loginLayoutPages = [
  "Auth/Login",
  "Auth/Register",
  "Auth/ForgotPassword",
  "Auth/ResetPassword",
  "admins/Sessions/New",
  "staffs/Sessions/New",
  "staffs/Passwords/New",
];

// SetupLayoutを使用するページ（ヘッダーあり、サイドバーなし）
const setupLayoutPages = [
  "users/Profiles/Setup",
  "users/Profiles/Verification",
];

// AdminLayoutを使用するページ（プレフィックスで判定）
const adminLayoutPrefix = "admins/";

// StaffLayoutを使用するページ（プレフィックスで判定）
const staffLayoutPrefix = "staffs/";

// Persistent Layout用の安定した関数参照
// Inertiaはlayout関数の参照が同一なら、レイアウトを再マウントせずページコンテンツだけ差し替える
const userLayout = (pageContent: React.ReactNode) => (
  <Layout>{pageContent}</Layout>
);
const loginLayout = (pageContent: React.ReactNode) => (
  <LoginLayout>{pageContent}</LoginLayout>
);
const setupLayout = (pageContent: React.ReactNode) => (
  <SetupLayout>{pageContent}</SetupLayout>
);
const adminLayout = (pageContent: React.ReactNode) => (
  <AdminLayout>{pageContent}</AdminLayout>
);
const staffLayout = (pageContent: React.ReactNode) => (
  <StaffLayout>{pageContent}</StaffLayout>
);

createInertiaApp({
  resolve: async (name) => {
    const pages = import.meta.glob("../pages/**/*.tsx", { eager: false });
    const component = await pages[`../pages/${name}.tsx`]();
    const page = (component as any).default;

    // 完全にLayoutなしのページ
    if (noLayoutPages.includes(name)) {
      return page;
    }

    // LoginLayoutを使用するページ
    if (loginLayoutPages.includes(name)) {
      page.layout = loginLayout;
      return page;
    }

    // SetupLayoutを使用するページ（ヘッダーあり、サイドバーなし）
    if (setupLayoutPages.includes(name)) {
      page.layout = setupLayout;
      return page;
    }

    // AdminLayoutを使用するページ（admins/で始まるがログインページ以外）
    if (name.startsWith(adminLayoutPrefix) && !loginLayoutPages.includes(name)) {
      page.layout = adminLayout;
      return page;
    }

    // StaffLayoutを使用するページ（staffs/で始まるがログインページ以外）
    if (name.startsWith(staffLayoutPrefix) && !loginLayoutPages.includes(name)) {
      page.layout = staffLayout;
      return page;
    }

    // デフォルトでUserLayoutをラップ
    page.layout = userLayout;
    return page;
  },
  progress: {
    delay: 100,
    color: "hsl(var(--primary))",
    includeCSS: true,
    showSpinner: false,
  },
  setup({ el, App, props }) {
    createRoot(el).render(<App {...props} />);

    // 初回マウント時にログイン済みなら referrer_domain を flush
    const initialProps = (props as any)?.initialPage?.props;
    if (initialProps?.current_user_header) {
      flushReferrerDomain(getClient()).catch(() => {});
    }
  },
});

// ページ遷移後（=登録直後の welcome 等）にログイン済みになったタイミングで flush
router.on("success", (event: any) => {
  const sharedProps = event?.detail?.page?.props;
  if (sharedProps?.current_user_header) {
    flushReferrerDomain(getClient()).catch(() => {});
  }
});
